import { splitUrl } from "@repo/fika-shared/src/lib/slug"
import { useNavigate, useParams } from "@solidjs/router"
import { createQuery } from "@urql/solid"
import { format } from "date-fns"
import { createMemo, createSignal, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { Toggle } from "@/designSystem/components/toggle"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { NavigationWithButtons } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/get-title"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { toast } from "@/lib/toast"
import { createBlogRouter } from "@/routes/posts-router"
import { AudienceForm } from "./_audience-form"
import { PostEditorFragment } from "./post-editor-context/provider"
import { alertClass, alreadySentClass, formClass } from "./post-publish.css"

export function PostPublish() {
  const params = useParams()
  const id = createMemo(() => splitUrl(params.postId))
  const [publishing, setPublishing] = createSignal(false)

  const [postQueryResult] = createQuery({
    query: graphql(
      `query PostPublish($id: String!) {
          post(id: $id) {
            ...PostPublishFragment
          }
        },
      `,
      [PostPublishFragment],
    ),
    variables: createMemo(() => ({ id: id() ?? "" })),
    pause: createMemo(() => !id()),
  })

  return (
    <MainLayout
      hue="amber"
      header={
        <Show when={postQueryResult.data?.post}>
          {post => (
            <PublishNavigation post={post()} publishing={publishing()} />
          )}
        </Show>
      }
      query={postQueryResult}
    >
      {data => (
        <Show when={data?.post}>
          {post => (
            <SafePostPublish post={post()} onPublishingChange={setPublishing} />
          )}
        </Show>
      )}
    </MainLayout>
  )
}

const PostPublishFragment = graphql(`
  fragment PostPublishFragment on Post {
    id
    publishedAt
    sentAt
    mainLocale
    private
    publication {
      id
      subscribersCount
    }
    currentVersions {
      id
      locale
      title
    }
  }
`)

function PublishNavigation(props: {
  post: FragmentOf<typeof PostPublishFragment>
  publishing: boolean
}) {
  const navigate = useNavigate()
  const post = createFragment(PostPublishFragment, () => props.post)
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const mainVersion = createMemo(() =>
    post().currentVersions.find(v => v.locale === post().mainLocale),
  )
  const title = createMemo(() => getTitle(mainVersion()?.title ?? ""))

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post({ id: post().id, title: title() }).url,
      } as const,
    ]
  })

  const publishLabel = createMemo(() =>
    post().publishedAt
      ? LL().post.publishPage.update()
      : LL().post.publishPage.publish(),
  )

  const postPath = createMemo(() =>
    paths().post({ id: post().id, title: title() }),
  )

  return (
    <NavigationWithButtons
      hue="amber"
      buttons={buttons()}
      title={publishLabel()}
    >
      <Button
        hue="sand"
        kind="surface"
        onClick={() => navigate(postPath().url)}
      >
        {LL().common.back()}
      </Button>
      <Button
        hue="indigo"
        type="submit"
        form="publish-form"
        active={props.publishing}
        iconName="rocket"
      >
        {publishLabel()}
      </Button>
    </NavigationWithButtons>
  )
}

const PublishPostMutation = graphql(
  `mutation PublishPostFromPublishPage($id: ID!, $sendEmail: Boolean!, $sendToAll: Boolean!, $private: Boolean!) {
    publishPost(id: $id, sendEmail: $sendEmail, sendToAll: $sendToAll, private: $private) {
      ... on MutationPublishPostSuccess {
        data {
          ...PostEditorFragment
        }
      }
      ...ErrorFragment
    }
  }`,
  [PostEditorFragment, ErrorFragment],
)

const SendTestPostEmailMutation = graphql(
  `mutation SendTestPostEmailFromPublish($id: ID!) {
    sendTestPostEmail(id: $id) {
      ... on MutationSendTestPostEmailSuccess {
        data
      }
      ...ErrorFragment
    }
  }`,
  [ErrorFragment],
)

function SafePostPublish(props: {
  post: FragmentOf<typeof PostPublishFragment>
  onPublishingChange: (publishing: boolean) => void
}) {
  const navigate = useNavigate()
  const post = createFragment(PostPublishFragment, () => props.post)
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const mainVersion = createMemo(() =>
    post().currentVersions.find(v => v.locale === post().mainLocale),
  )
  const title = createMemo(() => getTitle(mainVersion()?.title ?? ""))

  const [audienceSettings, setAudienceSettings] = createSignal({
    private: post().private ?? false,
  })

  const [formEmail, setFormEmail] = createSignal(!post().sentAt)
  const [testEmailSent, setTestEmailSent] = createSignal(false)

  const [, publishPost] = createMutationWithErrorHandling(
    PublishPostMutation,
    "publishPost",
  )

  const [sendTestEmailState, sendTestEmail] = createMutationWithErrorHandling(
    SendTestPostEmailMutation,
    "sendTestPostEmail",
  )

  const isAudienceRestricted = createMemo(() => {
    return audienceSettings().private
  })

  const totalSubscriberCount = createMemo(
    () => post().publication.subscribersCount ?? 0,
  )

  const effectiveRecipientCount = createMemo(() => {
    if (!isAudienceRestricted()) return totalSubscriberCount()
    return totalSubscriberCount()
  })

  const isSending = createMemo(() => formEmail())

  async function publish() {
    props.onPublishingChange(true)
    try {
      const isFirstPublish = !post().publishedAt
      const audience = audienceSettings()

      await publishPost({
        id: post().id,
        sendEmail: formEmail(),
        sendToAll: true,
        private: audience.private,
      })

      const postPath = paths().post({ id: post().id, title: title() })
      if (isFirstPublish) {
        navigate(postPath.publishShare())
      } else {
        toast.success(LL().post.updated())
        navigate(postPath.url)
      }
    } finally {
      props.onPublishingChange(false)
    }
  }

  async function handleSendTestEmail() {
    const result = await sendTestEmail({ id: post().id })

    if (!result.success) {
      toast.error(LL().post.publishPage.testEmailError())
      return
    }

    setTestEmailSent(true)
    toast.success(LL().post.publishPage.testEmailSent())
  }

  return (
    <div class={formClass}>
      <form
        id="publish-form"
        onSubmit={e => {
          e.preventDefault()
          e.stopPropagation()
          publish()
        }}
      >
        <Section
          title={LL().post.publishPage.audienceTitle()}
          description={LL().post.publishPage.audienceDescription()}
          hue="amber"
          iconName="user"
        >
          <Well hue="sand">
            <Box gap="12">
              <AudienceForm
                post={post()}
                onAudienceChange={setAudienceSettings}
              />
            </Box>
          </Well>
        </Section>
        <Section
          title={LL().post.publishPage.delivery()}
          description={LL().post.publishPage.deliveryDescription()}
          hue="amber"
          iconName="send"
        >
          <Well hue="sand">
            <Box gap="12">
              <Toggle
                label={LL().post.publishPage.sendViaEmail()}
                hue="amber"
                checked={formEmail()}
                onChange={setFormEmail}
              />
              <Show when={post().sentAt}>
                {sentAt => (
                  <div class={alreadySentClass}>
                    {LL().post.publishPage.alreadySent({
                      date: format(sentAt(), "PPp"),
                    })}
                  </div>
                )}
              </Show>
              <Show when={isSending()}>
                <div class={alertClass({ hue: "amber" })}>
                  <Icon name="mail" color="amber600" size="24" />
                  {LL().post.publishPage.subscribersWillReceive(
                    effectiveRecipientCount(),
                  )}
                  <div style={{ "margin-left": "auto" }}>
                    <Button
                      hue="amber"
                      kind="surface"
                      size="m"
                      onClick={handleSendTestEmail}
                      active={sendTestEmailState.fetching}
                      iconName={testEmailSent() ? "check" : "mail"}
                      type="button"
                      disabled={testEmailSent()}
                    >
                      {LL().post.publishPage.sendTestToMyself()}
                    </Button>
                  </div>
                </div>
              </Show>
            </Box>
          </Well>
        </Section>
      </form>
    </div>
  )
}
